import { createContext, useReducer } from 'react'
import PropTypes from 'prop-types'

/**
 * Type definition for the notification context, managing notifications and API response handling.
 *
 * @property {any[]} notifications - An array of notifications to be managed within the context.
 * @property {(apiResponse: any, title: string) => void} apiError - Function to handle API error responses,
 * dispatching error notifications.
 * @property {(apiResponse: any, title: string, message: string) => void} apiSuccess - Function to handle successful
 * API responses, dispatching success notifications.
 */
interface NotificationContextType {
    notifications: any[]
    apiError: (apiResponse: any, title: string) => void
    apiInfo: (apiResponse: any, title: string, message: string) => void
    apiSuccess: (apiResponse: any, title: string, message: string) => void
    showInfo: (title: string, message: string) => void
    showNotification: (
        type: 'success' | 'error' | 'info' | 'warning',
        message: string,
        title?: string
    ) => void
}

/**
 * Initial state object for the notification context.
 *
 * @type {Object}
 * @property {Array} notifications - Array to hold notification objects.
 * @property {Function} apiError - Function to handle API error notifications.
 * @property {Function} apiSuccess - Function to handle API success notifications.
 * @property {Function} showNotification - Function to display a notification with a specified type, title, and message.
 */
const initialState = {
    notifications: [],
    apiError: () => {},
    apiInfo: () => {},
    apiSuccess: () => {},
    showInfo: () => {},
    showNotification: () => {}
}

/**
 * Handles state transitions based on action type using the handlers map.
 *
 * @param {Object} state - The current state of the context.
 * @param {Object} action - The action object containing type and payload.
 * @returns {Object} The new state after applying the corresponding handler function or the current state if no handler is found.
 */
const reducer = (state, action) =>
    handlers[action.type] ? handlers[action.type](state, action) : state

/**
 * Creates a React context for notifications, initialized with the given state.
 *
 * @constant {React.Context<NotificationContextType>} NotificationContext - The context to provide state and dispatch
 * functionality for notifications.
 */
export const NotificationContext =
    createContext<NotificationContextType>(initialState)

/**
 * Object containing handler functions for notification actions in a state management pattern.
 *
 * Each property corresponds to a specific action type, and the function updates the state
 * based on the action's payload.
 */
const handlers = {
    ADD_NOTIFICATION: (state, action) => {
        const { type, title, message } = action.payload

        const notifications = [...state.notifications]

        const key = `${type}_${title}_${message}}`

        if (!notifications.find((notification) => notification.key === key)) {
            notifications.push({
                key,
                type,
                title,
                message
            })
        }

        return {
            ...state,
            notifications
        }
    },
    REMOVE_NOTIFICATION: (state, action) => {
        const { key } = action.payload

        return {
            ...state,
            notifications: state.notifications.filter(
                (notification) => notification.key !== key
            )
        }
    }
}

/**
 * Provides notification context and handlers for managing notifications.
 *
 * @param {Object} props - Properties passed to the provider component.
 * @returns {JSX.Element} React component that wraps children with notification context.
 */
export const NotificationProvider = (props) => {
    const { children } = props

    const [state, dispatch] = useReducer(reducer, initialState)

    const apiError = (apiResponse, title) => {
        const { statusCode, errorCode, errorMessage } = apiResponse || {}

        if (!errorCode || statusCode <= 400) return

        dispatch({
            type: 'ADD_NOTIFICATION',
            payload: {
                type: 'error',
                title: `${title}`,
                message: ` ${errorMessage} `
            }
        })
    }

    /**
     * Handles successful API responses by dispatching a success notification if there are no errors.
     *
     * @param {object} apiResponse - The response object from the API, containing statusCode, errorCode, and errorMessage.
     * @param {string} title - The title for the success notification.
     * @param {string} message - The message for the success notification.
     * @returns {void} Doesn't return a value, but dispatches a notification action if conditions are met.
     */
    const apiSuccess = (apiResponse, title, message) => {
        const { statusCode, errorCode, errorMessage } = apiResponse || {}
        if (errorCode || statusCode > 200 || errorMessage) return

        dispatch({
            type: 'ADD_NOTIFICATION',
            payload: { type: 'success', title, message }
        })
    }

    /**
     * Adds an informational notification if there is no error in the API response.
     *
     * @param {Object} apiResponse - The API response object containing status code, error code, and error message.
     * @param {string} title - The title for the notification.
     * @param {string} message - The message for the notification.
     * @returns {void} Does not return a value.
     */
    const apiInfo = (apiResponse, title, message) => {
        const { statusCode, errorCode, errorMessage } = apiResponse || {}
        if (errorCode || statusCode > 200 || errorMessage) return

        dispatch({
            type: 'ADD_NOTIFICATION',
            payload: { type: 'info', title, message }
        })
    }

    /**
     * Dispatches an action to add an informational notification with a specified title and message.
     *
     * @param {string} title - The title of the notification.
     * @param {string} message - The message body of the notification.
     */
    const showInfo = (title, message) => {
        dispatch({
            type: 'ADD_NOTIFICATION',
            payload: { type: 'info', title, message }
        })
    }

    /**
     * Dispatches a success notification with the given title and message.
     *
     * @param {string} title - The title of the success notification.
     * @param {string} message - The message content of the success notification.
     */
    const showSuccess = (title, message) => {
        dispatch({
            type: 'ADD_NOTIFICATION',
            payload: { type: 'success', title, message }
        })
    }

    /**
     * Removes a notification by dispatching an action with the specified key.
     *
     * @param {string} key - The unique key identifying the notification to be removed.
     */
    const removeNotification = (key) => {
        dispatch({
            type: 'REMOVE_NOTIFICATION',
            payload: { key }
        })
    }

    /**
     * Dispatches a notification action with the specified type, title, and message.
     *
     * @param {string} type - The type of notification ('success', 'error', 'info', etc.).
     * @param {string} message - The message content of the notification.
     * @param {string} title - The title of the notification.
     */
    const showNotification = (
        type: 'success' | 'error' | 'info' | 'warning',
        message: string,
        title?: string
    ) => {
        dispatch({
            type: 'ADD_NOTIFICATION',
            payload: {
                type,
                title: title || type.charAt(0).toUpperCase() + type.slice(1),
                message
            }
        })
    }

    return (
        <NotificationContext.Provider
            value={{
                ...state,
                apiError,
                apiSuccess,
                apiInfo,
                showInfo,
                showSuccess,
                removeNotification,
                showNotification
            }}
        >
            {children}
        </NotificationContext.Provider>
    )
}

NotificationProvider.propTypes = {
    children: PropTypes.node.isRequired
}

/**
 * Provides a consumer component for the NotificationContext.
 *
 * @constant {React.Consumer} NotificationConsumer - A component that subscribes to context changes and re-renders when
 * the context value changes.
 */
export const NotificationConsumer = NotificationContext.Consumer
